import { useState } from 'react';
import { api } from '../lib/api.js';
import { periodColor } from '../lib/ui.js';

// ─── Time Period Manager ───────────────────────────────────────────────────────
export function TimePeriodManager({ timePeriods, onChange }) {
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', start_time: '', end_time: '', sort_order: '' });
  const [err, setErr] = useState('');

  function startEdit(p) { setEditing(p.id); setEditForm({ name: p.name, start_time: p.start_time||'', end_time: p.end_time||'', sort_order: p.sort_order }); setErr(''); }

  async function handleUpdate(id) {
    await api('PUT', `/api/time-periods/${id}`, editForm);
    setEditing(null); onChange();
  }

  async function handleAdd(e) {
    e.preventDefault();
    setErr('');
    try {
      await api('POST', '/api/time-periods', addForm);
      setShowAdd(false); setAddForm({ name:'', start_time:'', end_time:'', sort_order:'' }); onChange();
    } catch(ex) { setErr(ex.message); }
  }

  async function handleDelete(id) {
    setErr('');
    try {
      await api('DELETE', `/api/time-periods/${id}`);
      onChange();
    } catch(ex) { setErr(ex.message); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-gray-500">{timePeriods.length} time periods</p>
          <p className="text-xs text-gray-400 mt-0.5">Tasks reference these periods. Reorder by changing sort order.</p>
        </div>
        <button onClick={() => setShowAdd(s=>!s)}
          className="bg-crimson text-white text-sm px-4 py-2 rounded-lg font-medium hover:bg-crimson-dark active:scale-95 transition-all">
          + New Period
        </button>
      </div>
      {err && <p className="text-red-600 text-sm mb-3 bg-red-50 rounded-lg px-3 py-2">{err}</p>}
      {showAdd && (
        <form onSubmit={handleAdd} className="bg-white rounded-xl border border-gray-200 p-4 mb-4 space-y-3 fade-in shadow-sm">
          <h3 className="font-semibold text-sm text-ink">New Time Period</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={addForm.name} onChange={e=>setAddForm(f=>({...f,name:e.target.value}))} required
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
              placeholder="Name (e.g. Pre-Opening)" />
            <input value={addForm.sort_order} onChange={e=>setAddForm(f=>({...f,sort_order:e.target.value}))} type="number"
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
              placeholder="Sort order (e.g. 0)" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input value={addForm.start_time} onChange={e=>setAddForm(f=>({...f,start_time:e.target.value}))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
              placeholder="Start time (e.g. 4:00 AM)" />
            <input value={addForm.end_time} onChange={e=>setAddForm(f=>({...f,end_time:e.target.value}))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-ink bg-white focus:outline-none"
              placeholder="End time (e.g. 5:30 AM)" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={()=>setShowAdd(false)} className="flex-1 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 font-medium">Cancel</button>
            <button type="submit" className="flex-1 py-2 rounded-lg bg-crimson text-white text-sm font-semibold hover:bg-crimson-dark">Add Period</button>
          </div>
        </form>
      )}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="divide-y divide-gray-100">
          {timePeriods.map((p, i) => (
            <div key={p.id} className="px-4 py-3">
              {editing === p.id ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input value={editForm.name} onChange={e=>setEditForm(f=>({...f,name:e.target.value}))}
                      className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-ink bg-white focus:outline-none"
                      placeholder="Name" />
                    <input value={editForm.sort_order} onChange={e=>setEditForm(f=>({...f,sort_order:e.target.value}))} type="number"
                      className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-ink bg-white focus:outline-none"
                      placeholder="Sort order" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input value={editForm.start_time} onChange={e=>setEditForm(f=>({...f,start_time:e.target.value}))}
                      className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-ink bg-white focus:outline-none"
                      placeholder="Start time" />
                    <input value={editForm.end_time} onChange={e=>setEditForm(f=>({...f,end_time:e.target.value}))}
                      className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-ink bg-white focus:outline-none"
                      placeholder="End time" />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={()=>setEditing(null)} className="flex-1 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600">Cancel</button>
                    <button onClick={()=>handleUpdate(p.id)} className="flex-1 py-1.5 rounded-lg bg-crimson text-white text-xs font-semibold">Save</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className={`period-badge px-2 py-1 rounded-md ${periodColor(p.name, i)}`}>{p.name}</span>
                  <span className="text-xs text-gray-400 flex-1">
                    {p.start_time && p.end_time ? `${p.start_time} – ${p.end_time}` : 'No times set'} · order {p.sort_order}
                  </span>
                  <div className="flex gap-1.5">
                    <button onClick={()=>startEdit(p)} className="px-2.5 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-all">Edit</button>
                    <button onClick={()=>handleDelete(p.id)} className="px-2.5 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all">Del</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
